import { useCallback, useMemo, useRef } from 'react';
import { groupAndSortEmojiReactions } from '../utils/emojiReactionUtils';
import {
    EmojiReactionGroup,
    EmojiReactionWithInfo,
} from '@/common/types/commonTypes';
import { useAuthStore } from '@/stores/authStore';
import { useFeedbackReactionNotification } from '@/features/notification/services/factoryNotificationServices';
import { notificationService } from '@/features/notification/services/notificationServices';
import { useDeleteNotification } from '@/features/notification/services/useNotificationQueries';
import { limitNotificationContentLength } from '@/features/notification/utils/notificationUtils';

/**
 * 이모지 리액션을 이모지 코드별로 그룹화하고 등록 시간순으로 정렬해 반환하는 훅
 * 동일한 이모지별로 사용자를 그룹화하고 가장 오래된 리액션이 먼저 표시되도록 정렬
 * @param reactions 원본 이모지 리액션 목록
 * @returns 이모지별로 그룹화되고 등록 시간순으로 정렬된 리액션 그룹 배열
 */
export function useGroupedEmojiReactions(
    reactions: EmojiReactionWithInfo[]
): EmojiReactionGroup[] {
    return useMemo(() => groupAndSortEmojiReactions(reactions), [reactions]);
}

/**
 * 피드백 이모지 리액션 알림 제어 훅
 * 사용자가 리액션을 추가/제거할 때 알림을 생성하거나 업데이트함
 * 디바운싱된 알림 처리 포함
 * 데일리스크럼 등 다른 엔티티에 대한 처리 X (피드백 엔티티에 대한 처리만 지원)
 * @param entityId 피드백 ID
 * @param notificationConfig 알림 설정
 * @returns startNotificationTimer - 알림 타이머 시작 함수
 */
export function useFeedbackReactionNotificationWatcher(
    entityId: string,
    notificationConfig?: {
        notificationReceiverId: string;
        feedbackReceiverId: string;
        feedbackContent: string;
        workspaceId: string;
    }
) {
    const user = useAuthStore(state => state.user);
    const deleteNotificationById = useDeleteNotification();
    const { createFeedbackReactionNotification } =
        useFeedbackReactionNotification();

    // 디바운싱과 즉시 처리를 위한 refs
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingReactionsRef = useRef<EmojiReactionWithInfo[] | null>(null);
    const isProcessingRef = useRef(false);

    // 최신 값들을 ref로 저장하여 함수 재생성 방지
    const latestValuesRef = useRef({
        notificationConfig,
        user,
        entityId,
        deleteNotificationById,
        createFeedbackReactionNotification,
    });

    // ref 값들을 최신으로 유지
    latestValuesRef.current = {
        notificationConfig,
        user,
        entityId,
        deleteNotificationById,
        createFeedbackReactionNotification,
    };

    // 케이스별 알림 처리
    const handleReactionNotification = useCallback(
        async (emojiReactions: EmojiReactionWithInfo[]) => {
            if (isProcessingRef.current) return; // 중복 요청 방지
            isProcessingRef.current = true;

            try {
                const {
                    notificationConfig,
                    user,
                    entityId,
                    deleteNotificationById,
                    createFeedbackReactionNotification,
                } = latestValuesRef.current;

                if (!notificationConfig || !user?.id) return;
                const currentUserReactions = emojiReactions
                    .filter(r => r.user?.id === user?.id)
                    .map(r => r.emoji_code);

                const uniqueEmojiCodesByUser = Array.from(
                    new Set(currentUserReactions)
                );

                // 기존 알림 체크
                const existingNotification =
                    await notificationService.checkExistingFeedbackReactionNotification(
                        {
                            userId: notificationConfig.notificationReceiverId,
                            feedbackId: entityId,
                        }
                    );

                if (!existingNotification) {
                    console.error('기존 알림 조회 실패');
                    return;
                }

                // 1. 리액션 모두 삭제 + 기존 알림 존재 → 알림 삭제 (기존 알림 없으면 그냥 리턴)
                if (uniqueEmojiCodesByUser.length === 0) {
                    if (
                        existingNotification.isExisting &&
                        existingNotification.notificationId
                    ) {
                        await deleteNotificationById.mutateAsync({
                            notificationId: existingNotification.notificationId,
                        });
                    }
                    return;
                }

                // 알림 콘텐츠 생성
                const notificationContent = {
                    reaction_content: uniqueEmojiCodesByUser,
                    feedback_id: entityId,
                    is_feedback_receiver:
                        notificationConfig.feedbackReceiverId ===
                        notificationConfig.notificationReceiverId,
                    feedback_content: limitNotificationContentLength(
                        notificationConfig.feedbackContent
                    ),
                    workspace_id: notificationConfig.workspaceId,
                    reactioner_id: user.id,
                    reactioner_name: user.name,
                    reactioner_image_url: user.profile_image_url,
                };

                // 2. 기존 알림 존재 + (읽지 않음 OR 이모지 삭제만) → 알림 업데이트
                if (existingNotification.isExisting) {
                    const hasNewReaction = uniqueEmojiCodesByUser.some(
                        emoji =>
                            !existingNotification.reactionEmojiCodes?.includes(
                                emoji
                            )
                    );

                    // 읽지 않은 상태이거나, 새 이모지 추가 없이 기존 이모지만 삭제된 경우
                    if (!existingNotification.isRead || !hasNewReaction) {
                        if (existingNotification.notificationId) {
                            await notificationService.updateNotificationContent(
                                existingNotification.notificationId,
                                notificationContent
                            );
                        }
                        return;
                    }
                }

                // 3. 그외 (기존 알림 없음 OR 새 이모지 추가) → 새 알림 생성
                await createFeedbackReactionNotification({
                    userId: notificationConfig.notificationReceiverId,
                    notiContent: notificationContent,
                });
            } catch (error) {
                console.error('알림 처리 실패:', error);
            } finally {
                isProcessingRef.current = false;
            }
        },
        [] // 의존성 배열을 비워서 함수 재생성 방지
    );

    // 즉시 처리 함수 (컴포넌트 언마운트 시 호출)
    const processImmediately = useCallback(async () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        if (pendingReactionsRef.current) {
            await handleReactionNotification(pendingReactionsRef.current);
            pendingReactionsRef.current = null;
        }
    }, [handleReactionNotification]);

    // 알림 타이머 시작 함수 (외부에서 호출용)
    const startNotificationTimer = useCallback(
        (emojiReactions: EmojiReactionWithInfo[]) => {
            const { notificationConfig, user } = latestValuesRef.current;

            if (!notificationConfig || !user?.id) return;
            if (user.id === notificationConfig.notificationReceiverId) return;

            // 최신 상태 저장
            pendingReactionsRef.current = emojiReactions;

            // 기존 타이머 취소
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // 짧은 디바운싱 (500ms) 적용
            debounceTimerRef.current = setTimeout(() => {
                if (pendingReactionsRef.current) {
                    void handleReactionNotification(
                        pendingReactionsRef.current
                    );
                    pendingReactionsRef.current = null;
                }
            }, 500);
        },
        [handleReactionNotification]
    );

    return {
        startNotificationTimer,
        processImmediately, // 컴포넌트 언마운트 시 호출할 함수
    };
}
